import jsPDF from 'jspdf';
import { Cursor } from 'lib/classes/jsPdfCursor';
import { formatDate, formatMoney, validateProducts } from 'lib/functions/utils';
import { User } from 'services/redux/authSlice';

import { OperativeProfileData } from '../index';

export const generateOperativeProfilePDFComplete = (
  data: OperativeProfileData,
  imageBase64: string,
  user?: User,
) => {
  const showSgr = validateProducts(user, ['sgr']);
  const showCredit = validateProducts(user, ['credit']);
  const showConfirming = validateProducts(user, ['confirming']);
  const showFactoring = validateProducts(user, ['factoring']);

  const doc = new jsPDF();
  const c = new Cursor(doc);
  c.col12 = 70;
  c.col22 = 170;
  let yPosition = 20;

  // Título principal
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Perfil Operativo', 105, yPosition, { align: 'center' });
  yPosition += 10;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(data.businessName, 105, yPosition, { align: 'center' });
  yPosition += 15;

  c.y = yPosition;

  c.writeTitle('Resumen de Cuenta Corriente');

  const tempY = c.y;
  c.writeLine('Monto a Favor', true, 'col11');
  c.writeLineAndNewLine(formatMoney(data.inFavorTotal ?? 0), true, 'col12');
  for (const account of data.inFavorAccounts ?? []) {
    c.writeLine(account.name, false, 'col11');
    c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col12');
  }
  c.newLine();
  let tempY2 = c.y;

  c.moveYTo(tempY);
  c.writeLine('Monto en Contra', true, 'col21');
  c.writeLineAndNewLine(formatMoney(data.againstTotal ?? 0), true, 'col22');
  for (const account of data.againstAccounts ?? []) {
    if (account.total && account.total > 0) {
      c.writeLine(account.name, false, 'col21');
      c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col22');
    }
  }
  c.newLine();
  tempY2 = c.y > tempY ? c.y : tempY;

  c.moveYTo(tempY2);
  c.writeLine('Moras', true, 'col11');
  c.writeLineAndNewLine(formatMoney(data.arrearsTotal ?? 0), true, 'col12');
  for (const account of data.arrearsAccounts ?? []) {
    if (account.total && account.total > 0) {
      c.writeLine(account.name, false, 'col11');
      c.writeLineAndNewLine(formatMoney(account.total ?? 0), false, 'col12');
    }
  }
  c.newLine();

  c.writeTitle('Líneas de Crédito');

  if (showFactoring) {
    c.writeLine('Factoring', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.factoringCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.factoringCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.factoringCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.factoringCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.factoringCreditLine?.validUntil
          ? formatDate(data.factoringCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.factoringCreditLine.amount - data.factoringCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
      if (data.factoringCreditLine.observations) {
        c.writeLine('Observaciones', false, 'col11');
        c.writeParagraph(
          data.factoringCreditLine.observations ?? '',
          false,
          'col12',
        );
      } else {
        c.writeLine('Observaciones', false, 'col11');
        c.writeLineAndNewLine('--', false, 'col12');
      }
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showConfirming) {
    c.writeLine('Confirming', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.confirmingCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.confirmingCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.confirmingCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.confirmingCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.confirmingCreditLine?.validUntil
          ? formatDate(data.confirmingCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.confirmingCreditLine.amount -
            data.confirmingCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
      if (data.confirmingCreditLine.observations) {
        c.writeLine('Observaciones', false, 'col11');
        c.writeParagraph(
          data.confirmingCreditLine.observations ?? '',
          false,
          'col12',
        );
      } else {
        c.writeLine('Observaciones', false, 'col11');
        c.writeLineAndNewLine('--', false, 'col12');
      }
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showCredit) {
    c.writeLine('Créditos', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.creditCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.creditCreditLine && showCredit) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.creditCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.creditCreditLineUsedAmount ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.creditCreditLine?.validUntil
          ? formatDate(data.creditCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.creditCreditLine.amount - data.creditCreditLineUsedAmount,
        ),
        false,
        'col22',
      );
      if (data.creditCreditLine.observations) {
        c.writeLine('Observaciones', false, 'col11');
        c.writeParagraph(
          data.creditCreditLine.observations ?? '',
          false,
          'col12',
        );
      } else {
        c.writeLine('Observaciones', false, 'col11');
        c.writeLineAndNewLine('--', false, 'col12');
      }
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  if (showSgr) {
    c.writeLine('Garantías', true, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.sgrCreditLine?.amount ?? 0),
      true,
      'col12',
    );
    if (data.sgrCreditLine) {
      c.writeLine('Estado', false, 'col11');
      c.writeLine(data.sgrCreditLine?.status ?? '', false, 'col12');
      c.writeLine('Utilizado', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(data.sgrCreditLineUsage.totalUsage ?? 0),
        false,
        'col22',
      );
      c.writeLine('Válida hasta', false, 'col11');
      c.writeLine(
        data.sgrCreditLine?.validUntil
          ? formatDate(data.sgrCreditLine.validUntil)
          : '',
        false,
        'col12',
      );
      c.writeLine('Disponible', false, 'col21');
      c.writeLineAndNewLine(
        formatMoney(
          data.sgrCreditLine.amount - data.sgrCreditLineUsage.totalUsage,
        ),
        false,
        'col22',
      );
      if (data.sgrCreditLine.observations) {
        c.writeLine('Observaciones', false, 'col11');
        c.writeParagraph(data.sgrCreditLine.observations ?? '', false, 'col12');
      } else {
        c.writeLine('Observaciones', false, 'col11');
        c.writeLineAndNewLine('--', false, 'col12');
      }

      c.writeLine('Fiel Cumplimiento', false, 'col11');
      c.writeLine(
        `${formatMoney(data.sgrCreditLineUsage.faithfulComplianceUsage)} de ${formatMoney(
          data.sgrCreditLine.subProducts?.find(
            (sub) => sub.name === 'Fiel cumplimiento',
          )?.amount || 0,
        )}`,
        false,
        'col12',
      );
      c.writeLine('Seriedad de la Oferta', false, 'col21');
      c.writeLineAndNewLine(
        `${formatMoney(data.sgrCreditLineUsage.offerSeriousnessUsage)} de ${formatMoney(
          data.sgrCreditLine.subProducts?.find(
            (sub) => sub.name === 'Seriedad de oferta',
          )?.amount || 0,
        )}`,
        false,
        'col22',
      );
    } else {
      c.writeLineAndNewLine(
        'No hay línea de crédito disponible',
        false,
        'col11',
      );
    }
    c.newLine();
  }

  c.newPage();
  c.moveYTo(20);
  c.writeTitle('Insights');

  // Insights Factoring
  if (showFactoring) {
    c.writeLineAndNewLine('Factoring', true, 'col11');
    const tempY3 = c.y;
    c.writeLineAndNewLine('Documentos', true, 'col11');
    c.writeLine('Comprados históricos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringBoughtDocumentsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Pagados', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringPaidDocumentsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en fecha', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringActiveDocumentsOnDateQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en mora', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringActiveDocumentsInArrearsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLineAndNewLine('Deudores', true, 'col11');
    c.writeLine('Deudores históricos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringHistoricStakeholdersQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Deudores activos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringActiveStakeholdersQuantity.toString(),
      false,
      'col12',
    );
    c.writeLineAndNewLine('Promedios', true, 'col11');
    const miniTempY = c.y;
    c.writeLine('Promedio días de curse de operación', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringAverageCurseDays.toString(),
      false,
      'col12',
    );
    c.writeLine('Promedio tasa de curse de operación', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringAverageCurseRate.toString(),
      false,
      'col12',
    );
    c.moveYTo(miniTempY);
    c.writeLine('Promedio de pago (días desde F.E.)', false, 'col21');
    c.writeLineAndNewLine(
      data.insights.factoring.factoringAveragePaymentDays.toString(),
      false,
      'col22',
    );
    c.writeLine('Promedio monto documento', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringAverageBoughtDocumentAmount),
      false,
      'col22',
    );
    let tempY4 = c.y;
    c.moveYTo(tempY3);
    c.writeLineAndNewLine('Montos de documentos (sin mora)', true, 'col21');
    c.writeLine('Monto documentos comprados', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringBoughtDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos pagados', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringPaidDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en fecha', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringOnDateDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en mora', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringInArrearsDocumentAmount),
      false,
      'col22',
    );
    c.writeLineAndNewLine('Recaudaciones', true, 'col21');
    c.writeLine('Por cliente', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.factoring.factoringConciliatedByBusinessAmount),
      false,
      'col22',
    );
    c.writeLine('Por deudor', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(
        data.insights.factoring.factoringConciliatedByStakeholderAmount,
      ),
      false,
      'col22',
    );
    tempY4 = c.y > tempY4 ? c.y : tempY4;
    c.moveYTo(tempY4);
    c.newLine();
  }

  // Insights Confirming
  if (showConfirming) {
    c.writeLineAndNewLine('Confirming', true, 'col11');
    const tempY3 = c.y;
    c.writeLineAndNewLine('Documentos', true, 'col11');
    c.writeLine('Comprados históricos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingBoughtDocumentsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Pagados', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingPaidDocumentsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en fecha', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingActiveDocumentsOnDateQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en mora', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingActiveDocumentsInArrearsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLineAndNewLine('Proveedores', true, 'col11');
    c.writeLine('Proveedores históricos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingHistoricBusinessesQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Proveedores activos', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingActiveBusinessesQuantity.toString(),
      false,
      'col12',
    );
    c.writeLineAndNewLine('Promedios', true, 'col11');
    const miniTempY = c.y;
    c.writeLine('Promedio días de curse de operación', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingAverageCurseDays.toString(),
      false,
      'col12',
    );
    c.writeLine('Promedio tasa de curse de operación', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingAverageCurseRate.toString(),
      false,
      'col12',
    );
    c.moveYTo(miniTempY);
    c.writeLine('Promedio de pago (días desde F.E.)', false, 'col21');
    c.writeLineAndNewLine(
      data.insights.confirming.confirmingAveragePaymentDays.toString(),
      false,
      'col22',
    );
    c.writeLine('Promedio monto documento', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(
        data.insights.confirming.confirmingAverageBoughtDocumentAmount,
      ),
      false,
      'col22',
    );
    let tempY4 = c.y;
    c.moveYTo(tempY3);
    c.writeLineAndNewLine('Montos de documentos (sin mora)', true, 'col21');
    c.writeLine('Monto documentos comprados', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.confirming.confirmingBoughtDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos pagados', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.confirming.confirmingPaidDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en fecha', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.confirming.confirmingOnDateDocumentAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en mora', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.confirming.confirmingInArrearsDocumentAmount),
      false,
      'col22',
    );
    c.writeLineAndNewLine('Recaudaciones', true, 'col21');
    c.writeLine('Por proveedor', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(
        data.insights.confirming.confirmingConciliatedByBusinessAmount,
      ),
      false,
      'col22',
    );
    c.writeLine('Por cliente-deudor', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(
        data.insights.confirming.confirmingConciliatedByStakeholderAmount,
      ),
      false,
      'col22',
    );
    tempY4 = c.y > tempY4 ? c.y : tempY4;
    c.moveYTo(tempY4);
    c.newLine();
  }

  // Insights Credit
  if (showCredit) {
    c.writeLineAndNewLine('Créditos', true, 'col11');
    const tempY3 = c.y;
    c.writeLineAndNewLine('Documentos', true, 'col11');
    c.writeLine('Pagados', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.credit.creditPaidDocumentsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en fecha', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.credit.creditActiveDocumentsOnDateQuantity.toString(),
      false,
      'col12',
    );
    c.writeLine('Vigentes en mora', false, 'col11');
    c.writeLineAndNewLine(
      data.insights.credit.creditActiveDocumentsInArrearsQuantity.toString(),
      false,
      'col12',
    );
    c.writeLineAndNewLine('Promedios', true, 'col11');
    c.writeLine('Promedio monto documento', false, 'col11');
    c.writeLineAndNewLine(
      formatMoney(data.insights.credit.creditAverageDocumentAmount),
      false,
      'col12',
    );
    let tempY4 = c.y;
    c.moveYTo(tempY3);
    c.writeLineAndNewLine('Montos de documentos (sin mora)', true, 'col21');
    c.writeLine('Monto documentos pagados', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.credit.creditPaidDocumentsAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en fecha', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.credit.creditActiveDocumentsOnDateAmount),
      false,
      'col22',
    );
    c.writeLine('Monto documentos en mora', false, 'col21');
    c.writeLineAndNewLine(
      formatMoney(data.insights.credit.creditActiveDocumentsInArrearsAmount),
      false,
      'col22',
    );
    tempY4 = c.y > tempY4 ? c.y : tempY4;
    c.moveYTo(tempY4);
    c.newLine();
  }

  // Insights Extensions
  c.writeLineAndNewLine('Prórrogas', true, 'col11');
  let tempY5 = c.y;
  c.writeLineAndNewLine('Activas', true, 'col11');
  c.writeLine('Documentos', false, 'col11');
  c.writeLineAndNewLine(
    data.extensions.active.quantity.toString(),
    false,
    'col12',
  );
  c.writeLine('Monto documentos', false, 'col11');
  c.writeLineAndNewLine(
    formatMoney(data.extensions.active.amount),
    false,
    'col12',
  );
  let tempY6 = c.y;
  c.writeLineAndNewLine('Completadas', true, 'col11');
  c.writeLine('Documentos', false, 'col11');
  c.writeLineAndNewLine(
    data.extensions.completed.quantity.toString(),
    false,
    'col12',
  );
  c.writeLine('Monto documentos', false, 'col11');
  c.writeLineAndNewLine(
    formatMoney(data.extensions.completed.amount),
    false,
    'col12',
  );
  tempY6 = c.y > tempY6 ? c.y : tempY6;
  c.moveYTo(tempY6);
  c.newLine();

  // Insights Active Money
  c.writeLineAndNewLine('Cartera Vigente', true, 'col11');
  tempY5 = c.y;
  c.writeLineAndNewLine('Factura electrónica', true, 'col11');
  c.writeLine('Documentos', false, 'col11');
  c.writeLineAndNewLine(
    data.activeMoneyByType['electronic-invoice'].quantity.toString(),
    false,
    'col12',
  );
  c.writeLine('Monto documentos', false, 'col11');
  c.writeLineAndNewLine(
    formatMoney(data.activeMoneyByType['electronic-invoice'].amount),
    false,
    'col12',
  );
  c.writeLineAndNewLine('Factura con flujo de cheques', true, 'col11');
  c.writeLine('Documentos', false, 'col11');
  c.writeLineAndNewLine(
    data.activeMoneyByType['check-invoice'].quantity.toString(),
    false,
    'col12',
  );
  c.writeLine('Monto documentos', false, 'col11');
  c.writeLineAndNewLine(
    formatMoney(data.activeMoneyByType['check-invoice'].amount),
    false,
    'col12',
  );
  tempY6 = c.y;
  c.moveYTo(tempY5);
  c.writeLineAndNewLine('Orden de compra', true, 'col21');
  c.writeLine('Documentos', false, 'col21');
  c.writeLineAndNewLine(
    data.activeMoneyByType['buy-order'].quantity.toString(),
    false,
    'col22',
  );
  c.writeLine('Monto documentos', false, 'col21');
  c.writeLineAndNewLine(
    formatMoney(data.activeMoneyByType['buy-order'].amount),
    false,
    'col22',
  );
  c.writeLineAndNewLine('Total', true, 'col21');
  c.writeLine('Documentos', false, 'col21');
  c.writeLineAndNewLine(
    data.activeMoneyByType.total.quantity.toString(),
    false,
    'col22',
  );
  c.writeLine('Monto documentos', false, 'col21');
  c.writeLineAndNewLine(
    formatMoney(data.activeMoneyByType.total.amount),
    false,
    'col22',
  );
  tempY6 = c.y > tempY6 ? c.y : tempY6;
  c.moveYTo(tempY6);

  c.newPage();
  c.moveYTo(20);

  c.writeTitle('Documentos');
  if (data.factoringDocumentsAsClient.length > 0) {
    c.writeLineAndNewLine('Factoring (cliente)', true, 'col11');
    c.writeTable(
      [
        'Deudor',
        'Rut deudor',
        'Folio',
        'Monto',
        'Conciliado',
        'Mora',
        'Emisión',
        'Vencimiento',
      ],
      data.factoringDocumentsAsClient.map((doc: any) => [
        doc.stakeholderName,
        doc.stakeholderIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.factoringDocumentsAsStakeholder.length > 0) {
    c.writeLineAndNewLine('Factoring (deudor)', true, 'col11');
    c.writeTable(
      ['Cliente', 'Rut cliente', 'Folio', 'Monto', 'Emisión', 'Vencimiento'],
      data.factoringDocumentsAsStakeholder.map((doc: any) => [
        doc.businessName,
        doc.businessIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.creditDocuments.length > 0) {
    c.writeLineAndNewLine('Créditos', true, 'col11');
    c.writeTable(
      ['Folio', 'Monto', 'Conciliado', 'Mora', 'Vencimiento'],
      data.creditDocuments.map((doc: any) => [
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data.confirmingDocuments.length > 0) {
    c.writeLineAndNewLine('Confirming', true, 'col11');
    c.writeTable(
      [
        'Cliente',
        'Rut cliente',
        'Folio',
        'Monto',
        'Conciliado',
        'Mora',
        'Emisión',
        'Vencimiento',
      ],
      data.confirmingDocuments.map((doc: any) => [
        doc.businessName,
        doc.businessIdentifier,
        doc.folio,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatMoney(doc.arrearAmount),
        formatDate(doc.issuedDate),
        formatDate(doc.paymentDate),
      ]),
    );
  }
  if (data?.sgrDocuments?.length > 0) {
    c.writeLineAndNewLine('Garantías', true, 'col11');
    c.writeTable(
      ['Folio', 'Cliente', 'Rut cliente', 'Monto', 'Conciliado', 'Emisión'],
      data.sgrDocuments.map((doc: any) => [
        doc.folio,
        doc.businessName,
        doc.businessIdentifier,
        formatMoney(doc.amount),
        formatMoney(doc.amountConciliated),
        formatDate(doc.issuedDate),
      ]),
    );
  }
  if (data.pendingAccountsInFavor.length > 0) {
    c.writeLineAndNewLine('Cuentas por pagar', true, 'col11');
    c.writeTable(
      ['Id', 'Categoría', 'Observación', 'Fecha', 'Monto', 'Monto por pagar'],
      data.pendingAccountsInFavor.map((doc: any) => [
        doc.folio,
        doc.pendingAccountType,
        doc.pendingAccountObservation,
        formatDate(doc.issuedDate),
        formatMoney(doc.amount),
        formatMoney(doc.pendingAccountInFavorRemainingAmount),
      ]),
    );
  }
  if (data.pendingAccountsAgainst.length > 0) {
    c.writeLineAndNewLine('Cuentas por cobrar', true, 'col11');
    c.writeTable(
      ['Id', 'Categoría', 'Observación', 'Fecha', 'Monto', 'Monto por cobrar'],
      data.pendingAccountsAgainst.map((doc: any) => [
        doc.folio,
        doc.pendingAccountType,
        doc.pendingAccountObservation,
        formatDate(doc.issuedDate),
        formatMoney(doc.amount),
        formatMoney(doc.documentRemainingAmountNet),
      ]),
    );
  }
  // Pie de página
  const pageCount = (doc as any).internal.getNumberOfPages();
  const imgHeight = 5;

  const aspectRatio = 185 / 53;
  const imgWidth = imgHeight * aspectRatio;
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Página ${i} de ${pageCount}`,
      105,
      doc.internal.pageSize.height - 10,
      { align: 'center' },
    );
    doc.text(
      `Generado el: ${formatDate(new Date())}`,
      20,
      doc.internal.pageSize.height - 10,
    );
    doc.addImage(
      imageBase64,
      'PNG',
      doc.internal.pageSize.width - 5 - imgWidth,
      doc.internal.pageSize.height - 5 - imgHeight,
      imgWidth,
      imgHeight,
    );
  }

  // Guardar el PDF
  const fileName = `Perfil_Operativo_${data.businessName?.replace(/ /g, '_') || 'Cliente'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
